import {
  collection,
  doc,
  FieldPath,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { readCatalogProducts } from "@shared/operational-ai/operational-ai.catalog";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { authStore } from "@/core/store/auth.store";
import { db, functions, storage } from "@/core/config/firebase";
import {
  ApproveOperationalCollectionRequest,
  ApproveOperationalCollectionResponse,
  CreateOperationalWorkspaceInput,
  DeleteOperationalWorkspaceRequest,
  DeleteOperationalWorkspaceResponse,
  OPERATIONAL_AI_SCHEMA_VERSION,
  OperationalAttachment,
  OperationalAuditEntry,
  OperationalFieldEntry,
  OperationalJsonValue,
  OperationalMessage,
  OperationalPhaseDocument,
  OperationalPhaseId,
  OperationalProfile,
  ProcessOperationalCollectionRequest,
  ProcessOperationalCollectionResponse,
  TranscribeOperationalAudioRequest,
  TranscribeOperationalAudioResponse,
} from "@shared/operational-ai/operational-ai.types";
import {
  OPERATIONAL_PHASES,
  calculateOperationalCompleteness,
  createFieldEntry,
  emptyOperationalPhase,
  findOperationalField,
  operationalStoragePhase,
} from "@shared/operational-ai/operational-ai.schema";

const PROFILE_COLLECTION = "operational_ai_profiles";
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const MAX_DICTATION_SIZE = 15 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set<OperationalAttachment["mimeType"]>([
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/pdf",
]);

export interface OperationalWorkspaceSnapshot {
  profile: OperationalProfile;
  phases: OperationalPhaseDocument[];
  messages: OperationalMessage[];
}

function requireUserId(): string {
  const userId = authStore.getState().firebaseUser?.uid ?? authStore.getState().user?.uid;
  if (!userId) throw new Error("Debes iniciar sesión para configurar la IA.");
  return userId;
}

function profileRef(userId: string, workspaceId: string) {
  return doc(db, "usuarios", userId, PROFILE_COLLECTION, workspaceId);
}

function phaseRef(userId: string, workspaceId: string, phaseId: OperationalPhaseId) {
  return doc(db, "usuarios", userId, PROFILE_COLLECTION, workspaceId, "phases", phaseId);
}

function asProfile(value: unknown): OperationalProfile | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  return typeof record.id === "string" && typeof record.phoneId === "string"
    ? (value as OperationalProfile)
    : null;
}

function asPhase(value: unknown): OperationalPhaseDocument | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  return typeof record.phaseId === "string" && record.fields !== null && typeof record.fields === "object"
    ? (value as OperationalPhaseDocument)
    : null;
}

function asMessage(value: unknown): OperationalMessage | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  return typeof record.id === "string" && typeof record.text === "string"
    ? (value as OperationalMessage)
    : null;
}

function createId(prefix: string): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${random.replaceAll("-", "")}`;
}

export async function createOperationalWorkspace(input: CreateOperationalWorkspaceInput): Promise<OperationalWorkspaceSnapshot> {
  if (input.catalogProducts && (!input.catalogProducts.length || input.catalogProducts.length > 100 || readCatalogProducts(input.catalogProducts).length !== input.catalogProducts.length)) {
    throw new Error("Selecciona entre 1 y 100 productos válidos para el catálogo.");
  }
  const userId = requireUserId();
  const now = new Date().toISOString();
  const workspaceId = createId("opai");
  const phases = OPERATIONAL_PHASES.map((phase) => emptyOperationalPhase(phase.id, now));
  const productPhase = phases.find((phase) => phase.phaseId === "producto");

  if (productPhase) {
    if (input.catalogProducts) {
      const definition = findOperationalField("catalogo.productos");
      if (definition) productPhase.fields[definition.key] = createFieldEntry(definition, input.catalogProducts, now, { id: createId("src"), type: "inventario", reference: "catalogo", label: "Productos seleccionados" }, true);
    }
    const inventorySource = { id: createId("src"), type: "inventario" as const, reference: input.productId ?? "manual", label: "Producto seleccionado en inventario" };
    const seeds: Array<[string, OperationalJsonValue]> = [
      ["producto.nombre", input.productName],
      ...(input.productImageUrl ? [["producto.imagen", input.productImageUrl] as [string, OperationalJsonValue]] : []),
      ...(input.productCategory ? [["producto.categoria", input.productCategory] as [string, OperationalJsonValue]] : []),
      ...(input.productSku ? [["producto.identificadores", `SKU: ${input.productSku}`] as [string, OperationalJsonValue]] : []),
    ];
    for (const [key, value] of seeds) {
      const definition = findOperationalField(key);
      if (definition) productPhase.fields[key] = createFieldEntry(definition, value, now, inventorySource, true);
    }
  }

    const logisticsPhase = phases.find((phase) => phase.phaseId === "logistica");
  if (logisticsPhase) {
    const logisticsSource = { id: createId("src"), type: "mensaje" as const, reference: "default_logistica_init", label: "Valores iniciales de logística" };
    const defaultCouriers: OperationalJsonValue = [
      {
        empresa: "cargo_expreso",
        tipo_servicio: "Cobro Contra Entrega (COD)",
        costo_paquete: 35,
        comision_cod: "4.00%",
        peso_base: 5,
        costo_libra_extra: 0,
        bloque_peso_extra: 1,
      },
    ];
    const defaultPolicy: OperationalJsonValue = {
      selectedCourier: "cargo_expreso",
      policies: {
        cargo_expreso: {
          opcion: "tarifa_fija",
          subOpcionGratis: "nacional",
          zonasGratis: [],
          costoBase: 35,
          costoDepartamentos: 45,
          montoMinimo: "",
          detalle: "",
        },
      },
      opcion: "tarifa_fija",
      subOpcionGratis: "nacional",
      zonasGratis: [],
      costoBase: 35,
      costoDepartamentos: 45,
      montoMinimo: "",
      detalle: "",
    };

    const seeds: Array<[string, OperationalJsonValue]> = [
      ["logistica.detalles_couriers", defaultCouriers],
      ["logistica.politica_envio", defaultPolicy],
    ];

    for (const [key, value] of seeds) {
      const definition = findOperationalField(key);
      if (definition) logisticsPhase.fields[key] = createFieldEntry(definition, value, now, logisticsSource, false);
    }
  }

  const completeness = calculateOperationalCompleteness(phases);
  const profile: OperationalProfile = {
    schemaVersion: OPERATIONAL_AI_SCHEMA_VERSION,
    id: workspaceId,
    ownerId: userId,
    phoneId: input.phoneId,
    phoneLabel: input.phoneLabel,
    productId: input.productId,
    productName: input.productName,
    productImageUrl: input.productImageUrl,
    catalogImageUrls: input.catalogProducts
      ? input.catalogProducts.map((p) => p.mainImageUrl).filter(Boolean)
      : (input.productImageUrl ? [input.productImageUrl] : []),
    country: input.country,
    salesTechnique: input.salesTechnique,
    experience: "quetzal_ia_operativa_v3",
    status: "en_progreso",
    currentPhase: "producto",
    visitedPhases: ["producto"],
    completeness: completeness.percentage,
    criticalMissing: completeness.criticalMissing,
    activationStatus: "pendiente_configuracion",
    createdAt: now,
    updatedAt: now,
    approvedAt: null,
  };

  const welcome: OperationalMessage = {
    id: createId("msg"),
    role: "asistente",
    text: `Vamos a preparar la inteligencia de ${input.productName}. Puedes contarme todo lo que ya tengas sobre tu empresa, políticas y producto; también puedes adjuntar imágenes o PDF. Yo lo organizaré y te pediré únicamente lo crítico que falte.`,
    state: "completado",
    attachments: [],
    createdAt: now,
  };

  const batch = writeBatch(db);
  batch.set(profileRef(userId, workspaceId), profile);
  for (const phase of phases) batch.set(phaseRef(userId, workspaceId, phase.phaseId), phase);
  batch.set(doc(db, "usuarios", userId, PROFILE_COLLECTION, workspaceId, "messages", welcome.id), welcome);
  await batch.commit();

  return { profile, phases, messages: [welcome] };
}

export async function listOperationalWorkspaces(phoneId: string): Promise<OperationalProfile[]> {
  const userId = requireUserId();
  const profilesQuery = query(
    collection(db, "usuarios", userId, PROFILE_COLLECTION),
    where("phoneId", "==", phoneId),
    limit(12),
  );
  const snapshot = await getDocs(profilesQuery);
  return snapshot.docs
    .map((item) => asProfile(item.data()))
    .filter((item): item is OperationalProfile => item !== null)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export async function loadOperationalWorkspace(workspaceId: string): Promise<OperationalWorkspaceSnapshot> {
  const userId = requireUserId();
  const [profileSnapshot, politicasSnapshot, ...phaseSnapshots] = await Promise.all([
    getDoc(profileRef(userId, workspaceId)),
    getDoc(phaseRef(userId, workspaceId, "politicas")),
    ...OPERATIONAL_PHASES.map((phase) => getDoc(phaseRef(userId, workspaceId, phase.id))),
  ]);
  const profile = asProfile(profileSnapshot.data());
  if (!profile) throw new Error("No se encontró esta configuración de IA.");
  const now = new Date().toISOString();
  const phases = phaseSnapshots.map((item, index) => asPhase(item.data()) ?? emptyOperationalPhase(OPERATIONAL_PHASES[index].id, now));

  // Migración y consolidación transparente: Si existen políticas en phases/politicas, fusionarlas en phases/logistica
  const logisticaPhase = phases.find((p) => p.phaseId === "logistica");
  if (logisticaPhase && politicasSnapshot.exists()) {
    const polData = asPhase(politicasSnapshot.data());
    if (polData?.fields) {
      logisticaPhase.fields = { ...polData.fields, ...logisticaPhase.fields };
    }
  }

  // Sincronizar un documento de fase virtual 'politicas' en memoria si no está presente
  if (!phases.some((p) => p.phaseId === "politicas") && logisticaPhase) {
    const virtualPol = emptyOperationalPhase("politicas", now);
    for (const [key, entry] of Object.entries(logisticaPhase.fields)) {
      if (key.startsWith("politicas.")) {
        virtualPol.fields[key] = entry;
      }
    }
    phases.push(virtualPol);
  }

  const messagesQuery = query(
    collection(db, "usuarios", userId, PROFILE_COLLECTION, workspaceId, "messages"),
    orderBy("createdAt", "desc"),
    limit(50),
  );
  const messageSnapshot = await getDocs(messagesQuery);
  const messages = messageSnapshot.docs.map((item) => asMessage(item.data())).filter((item): item is OperationalMessage => item !== null).reverse();
  const completeness = calculateOperationalCompleteness(phases);
  return { profile: { ...profile, completeness: completeness.percentage, criticalMissing: completeness.criticalMissing }, phases, messages };
}

export async function saveOperationalField(
  workspace: OperationalWorkspaceSnapshot,
  phaseId: OperationalPhaseId,
  fieldKey: string,
  value: OperationalJsonValue,
): Promise<OperationalWorkspaceSnapshot> {
  const userId = requireUserId();
  const definition = findOperationalField(fieldKey);
  if (!definition || definition.derived || definition.phaseId !== phaseId) throw new Error("Este dato no pertenece a la fase seleccionada.");
  const now = new Date().toISOString();
  // Optimización de costos Firestore: los pasos del asistente de producto se concentran en phases/producto, y políticas en phases/logistica
  const targetDbPhaseId = operationalStoragePhase(phaseId);
  const phases = workspace.phases.map((phase) => ({ ...phase, fields: { ...phase.fields }, audit: [...phase.audit] }));
  let phase = phases.find((item) => item.phaseId === targetDbPhaseId);
  if (!phase) {
    phase = emptyOperationalPhase(targetDbPhaseId, now);
    phases.push(phase);
  }
  const previous = phase.fields[fieldKey];
  const isConfirmation = previous?.status === "propuesto" || previous?.status === "requiere_revision";
  const source = { id: createId("src"), type: "mensaje" as const, reference: "edicion_panel", label: "Editado por el dueño en el panel" };
  const next: OperationalFieldEntry = {
    key: fieldKey,
    value,
    status: previous && !isConfirmation ? "corregido" : "confirmado",
    filledBy: previous?.filledBy === "ia" ? "mixto" : "dueno",
    sources: [...(previous?.sources ?? []), source].slice(-10),
    updatedAt: now,
    requiresConfirmation: definition.requiresConfirmation,
    nature: definition.nature,
  };
  phase.fields[fieldKey] = next;
  phase.visited = true;
  phase.updatedAt = now;

  // Sincronización in-memory para la fase "politicas" si alguna vista la busca directamente
  if (phaseId === "politicas") {
    let polPhase = phases.find((item) => item.phaseId === "politicas");
    if (!polPhase) {
      polPhase = emptyOperationalPhase("politicas", now);
      phases.push(polPhase);
    }
    polPhase.fields[fieldKey] = next;
    polPhase.visited = true;
    polPhase.updatedAt = now;
  }
  const auditEntry: OperationalAuditEntry = { id: createId("audit"), fieldKey, actor: "dueno", action: previous && !isConfirmation ? "corregido" : "confirmado", occurredAt: now };
  phase.audit = [...phase.audit, auditEntry].slice(-50);
  const completeness = calculateOperationalCompleteness(phases);
  phase.completed = completeness.completedPhases.includes(phaseId);
  const profile: OperationalProfile = {
    ...workspace.profile,
    status: workspace.profile.status === "aprobado" ? "en_revision" : workspace.profile.status,
    currentPhase: phaseId,
    visitedPhases: Array.from(new Set([...workspace.profile.visitedPhases, phaseId])),
    completeness: completeness.percentage,
    criticalMissing: completeness.criticalMissing,
    activationStatus: "pendiente_configuracion",
    updatedAt: now,
    approvedAt: null,
  };
  const batch = writeBatch(db);
  const systemMessage: OperationalMessage = {
    id: createId("msg"),
    role: "sistema",
    text: `${previous && !isConfirmation ? "Actualizaste" : "Confirmaste"} “${definition.label}”.`,
    state: "completado",
    attachments: [],
    createdAt: now,
  };
  batch.set(phaseRef(userId, workspace.profile.id, targetDbPhaseId), { ...phase, fields: { [fieldKey]: next } }, {
    mergeFields: [...Object.keys(phase).filter(key => key !== "fields"), new FieldPath("fields", fieldKey)],
  });
  batch.set(profileRef(userId, workspace.profile.id), profile, { merge: true });
  batch.set(doc(db, "usuarios", userId, PROFILE_COLLECTION, workspace.profile.id, "messages", systemMessage.id), systemMessage);
  await batch.commit();
  return { ...workspace, profile, phases, messages: [...workspace.messages, systemMessage] };
}

async function validateAttachment(file: File): Promise<OperationalAttachment["mimeType"]> {
  if (!ALLOWED_MIME_TYPES.has(file.type as OperationalAttachment["mimeType"])) throw new Error(`El archivo ${file.name} no es PNG, JPG, WEBP o PDF.`);
  if (file.size <= 0 || file.size > MAX_ATTACHMENT_SIZE) throw new Error(`${file.name} supera el límite de 10 MB.`);
  const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
  const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  const isWebp = String.fromCharCode(...bytes.slice(0, 4)) === "RIFF" && String.fromCharCode(...bytes.slice(8, 12)) === "WEBP";
  const isPdf = String.fromCharCode(...bytes.slice(0, 5)) === "%PDF-";
  const signatureValid = file.type === "image/png" ? isPng : file.type === "image/jpeg" ? isJpeg : file.type === "image/webp" ? isWebp : isPdf;
  if (!signatureValid) throw new Error(`${file.name} no coincide con su tipo real y fue rechazado por seguridad.`);
  return file.type as OperationalAttachment["mimeType"];
}

export async function uploadOperationalAttachments(workspaceId: string, requestId: string, files: File[]): Promise<OperationalAttachment[]> {
  if (files.length > MAX_ATTACHMENTS) throw new Error(`Puedes adjuntar hasta ${MAX_ATTACHMENTS} archivos por mensaje.`);
  const userId = requireUserId();
  const attachments: OperationalAttachment[] = [];
  for (const file of files) {
    const mimeType = await validateAttachment(file);
    const safeName = file.name.normalize("NFKD").replace(/[^a-zA-Z0-9._-]/g, "_").slice(-100);
    const id = createId("att");
    const storagePath = `usuarios/${userId}/operational-ai/${workspaceId}/${requestId}/${id}_${safeName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file, { contentType: mimeType, customMetadata: { workspaceId, requestId } });
    const downloadUrl = await getDownloadURL(storageRef);
    attachments.push({ id, name: safeName, mimeType, size: file.size, storagePath, downloadUrl });
  }
  return attachments;
}

export async function processOperationalMessage(request: ProcessOperationalCollectionRequest): Promise<ProcessOperationalCollectionResponse> {
  requireUserId();
  const callable = httpsCallable<ProcessOperationalCollectionRequest, ProcessOperationalCollectionResponse>(functions, "procesarRecoleccionOperativa");
  const result = await callable(request);
  return result.data;
}

export async function transcribeOperationalAudio(audio: Blob, durationMs: number, context?: { fieldKey?: string; fieldLabel?: string; fieldRole?: string; productContext?: Record<string, string>; }): Promise<string> {
  requireUserId();
  if (audio.size <= 0) throw new Error("No se detectó audio para transcribir.");
  if (audio.size > MAX_DICTATION_SIZE) throw new Error("El dictado es demasiado largo. Intenta grabarlo en partes más cortas.");
  const mimeType = audio.type.split(";")[0] as TranscribeOperationalAudioRequest["mimeType"];
  const allowedMimeTypes = new Set<TranscribeOperationalAudioRequest["mimeType"]>(["audio/webm", "audio/ogg", "audio/mp4", "audio/mpeg", "audio/wav"]);
  if (!allowedMimeTypes.has(mimeType)) throw new Error("El formato de audio de este navegador no es compatible.");
  const bytes = new Uint8Array(await audio.arrayBuffer());
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  const callable = httpsCallable<TranscribeOperationalAudioRequest, TranscribeOperationalAudioResponse>(functions, "transcribirAudioOperativo");
  const result = await callable({
    audioBase64: btoa(binary),
    mimeType,
    durationMs,
    fieldKey: context?.fieldKey,
    fieldLabel: context?.fieldLabel,
    fieldRole: context?.fieldRole,
    productContext: context?.productContext,
  });
  return result.data.transcript.trim();
}

export async function approveOperationalWorkspace(workspaceId: string): Promise<ApproveOperationalCollectionResponse> {
  requireUserId();
  const callable = httpsCallable<ApproveOperationalCollectionRequest, ApproveOperationalCollectionResponse>(functions, "aprobarRecoleccionOperativa");
  const result = await callable({ workspaceId });
  return result.data;
}

export async function deleteOperationalMessages(workspaceId: string, messageIds: string[]): Promise<void> {
  const userId = requireUserId();
  const batch = writeBatch(db);
  for (const messageId of messageIds) {
    const docRef = doc(db, "usuarios", userId, PROFILE_COLLECTION, workspaceId, "messages", messageId);
    batch.delete(docRef);
  }
  await batch.commit();
}

export async function deleteOperationalWorkspace(workspaceId: string): Promise<void> {
  requireUserId();
  const callable = httpsCallable<DeleteOperationalWorkspaceRequest, DeleteOperationalWorkspaceResponse>(
    functions,
    "eliminarRecoleccionOperativa",
  );
  const result = await callable({ workspaceId });
  if (!result.data.deleted) throw new Error("El servidor no confirmó la eliminación completa.");
}

export function newOperationalRequestId(): string {
  return createId("req");
}

/**
 * Activa o desactiva un workspace de producto operativo para que la IA lo atienda o ignore
 */
export async function toggleOperationalWorkspaceActive(
  workspaceId: string,
  currentIsActive: boolean
): Promise<boolean> {
  const userId = requireUserId();
  if (!currentIsActive) {
    const snapshot = await loadOperationalWorkspace(workspaceId);
    if (snapshot.profile.status !== "aprobado" || !snapshot.profile.approvedAt || snapshot.profile.criticalMissing.length > 0) {
      throw new Error("Revisa y aprueba la configuración antes de activar este producto.");
    }
  }
  const newActivationStatus = currentIsActive ? "pendiente_configuracion" : "lista_para_ventas";
  await updateDoc(profileRef(userId, workspaceId), {
    activationStatus: newActivationStatus,
    updatedAt: new Date().toISOString(),
  });
  return !currentIsActive;
}

/**
 * Restablece por completo los datos de una fase operativa (ej: identidad) en Firestore y en el perfil
 */
export async function resetOperationalPhase(
  workspaceId: string,
  phaseId: OperationalPhaseId
): Promise<OperationalWorkspaceSnapshot> {
  const userId = requireUserId();
  const snapshot = await loadOperationalWorkspace(workspaceId);
  const now = new Date().toISOString();

  const phases = snapshot.phases.map((phase) => {
    if (phase.phaseId === phaseId) {
      return {
        ...phase,
        fields: {},
        completed: false,
        visited: false,
        updatedAt: now,
        audit: [
          ...phase.audit,
          {
            id: createId("audit"),
            fieldKey: `${phaseId}.reset`,
            actor: "dueno" as const,
            action: "corregido" as const,
            occurredAt: now,
          },
        ].slice(-50),
      };
    }
    return phase;
  });

  const completeness = calculateOperationalCompleteness(phases);
  const profile: OperationalProfile = {
    ...snapshot.profile,
    status: snapshot.profile.status === "aprobado" ? "en_revision" : snapshot.profile.status,
    activationStatus: "pendiente_configuracion",
    approvedAt: null,
    visitedPhases: snapshot.profile.visitedPhases.filter((p) => p !== phaseId),
    completeness: completeness.percentage,
    criticalMissing: completeness.criticalMissing,
    updatedAt: now,
  };

  const batch = writeBatch(db);
  const resetPhase = phases.find((p) => p.phaseId === phaseId);
  if (resetPhase) {
    batch.set(phaseRef(userId, workspaceId, phaseId), resetPhase);
  }
  batch.set(profileRef(userId, workspaceId), profile, { merge: true });
  await batch.commit();

  return {
    ...snapshot,
    profile,
    phases,
  };
}

/**
 * Restablece múltiples fases operativas (ej: identidad, protocolo, empresa, politicas)
 * en Firestore en una sola lectura y una sola transacción batch para optimizar costos y evitar colisiones.
 */
export async function resetOperationalPhases(
  workspaceId: string,
  phaseIds: OperationalPhaseId[]
): Promise<OperationalWorkspaceSnapshot> {
  const userId = requireUserId();
  const snapshot = await loadOperationalWorkspace(workspaceId);
  const now = new Date().toISOString();
  const phaseSet = new Set(phaseIds);

  const phases = snapshot.phases.map((phase) => {
    if (phaseSet.has(phase.phaseId)) {
      return {
        ...phase,
        fields: {},
        completed: false,
        visited: false,
        updatedAt: now,
        audit: [
          ...phase.audit,
          {
            id: createId("audit"),
            fieldKey: `${phase.phaseId}.reset`,
            actor: "dueno" as const,
            action: "corregido" as const,
            occurredAt: now,
          },
        ].slice(-50),
      };
    }
    return phase;
  });

  const completeness = calculateOperationalCompleteness(phases);
  const profile: OperationalProfile = {
    ...snapshot.profile,
    status: snapshot.profile.status === "aprobado" ? "en_revision" : snapshot.profile.status,
    activationStatus: "pendiente_configuracion",
    approvedAt: null,
    visitedPhases: snapshot.profile.visitedPhases.filter((p) => !phaseSet.has(p)),
    completeness: completeness.percentage,
    criticalMissing: completeness.criticalMissing,
    updatedAt: now,
  };

  const batch = writeBatch(db);
  for (const phaseId of phaseIds) {
    const resetPhase = phases.find((p) => p.phaseId === phaseId);
    if (resetPhase) {
      batch.set(phaseRef(userId, workspaceId, phaseId), resetPhase);
    }
  }
  batch.set(profileRef(userId, workspaceId), profile, { merge: true });
  await batch.commit();

  return {
    ...snapshot,
    profile,
    phases,
  };
}
